import { CloseOutlined } from '@ant-design/icons';
import { useCallback, useRef, useState } from 'react';
import AppButton from './AppButton';

const splitFileName = (name: string): { base: string; ext: string } => {
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex === -1) return { base: name, ext: '' };
  return { base: name.slice(0, dotIndex), ext: name.slice(dotIndex) };
};

export interface AppUploadProps {
  onChange?: (file: File | null) => void;
  accept?: string;
  disabled?: boolean;
  buttonStyle?: React.CSSProperties;
  buttonClassName?: string;
  className?: string;
}

const AppUpload = ({
  onChange,
  accept,
  disabled,
  buttonStyle,
  buttonClassName,
  className,
}: AppUploadProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dragCountRef = useRef(0);

  const handleSelect = (selected: File | null) => {
    setFile(selected);
    onChange?.(selected);
  };

  const handleRemove = () => {
    handleSelect(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      dragCountRef.current++;
      setIsDragging(true);
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCountRef.current--;
    if (dragCountRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCountRef.current = 0;
      setIsDragging(false);
      if (disabled) return;

      const droppedFile = e.dataTransfer.files?.[0];
      if (droppedFile) {
        handleSelect(droppedFile);
      }
    },
    [disabled]
  );

  return (
    <div
      className={`flex items-center gap-3 w-full transition-colors ${
        isDragging ? 'border border-dashed border-blue-400 bg-blue-50 rounded-md px-3 py-2' : ''
      } ${className ?? ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        disabled={disabled}
        onChange={e => handleSelect(e.target.files?.[0] ?? null)}
      />
      <AppButton
        type="primary"
        disabled={disabled}
        style={buttonStyle}
        className={buttonClassName}
        onClick={() => inputRef.current?.click()}
      >
        ファイルを選択
      </AppButton>
      {file ? (
        <span className="flex min-w-0 text-sm text-gray-800" title={file.name}>
          <span className="truncate">{splitFileName(file.name).base}</span>
          <span className="shrink-0">{splitFileName(file.name).ext}</span>
        </span>
      ) : (
        <span className="text-sm text-gray-400" />
      )}
      {file && (
        <CloseOutlined
          className="text-red-500 hover:text-red-700 cursor-pointer transition-colors text-xs"
          onClick={() => !disabled && handleRemove()}
        />
      )}
    </div>
  );
};

export default AppUpload;
